import React from 'react';

/**
 * Library for localization, string formatting etc.
 * @packageDocumentation
 */
import type { LanguageProperty } from '../../components';
import { isEmptyString } from '../../util';
import { isEmpty } from 'lodash';

/**
 * Format a string (e.g. "Patient {0}")
 *
 * This works like Ext.String.format in extjs.
 *
 *
 * @param fmt a format string with replaceable values of the form "{0}, {1}, ..."
 * @param values The values to use as replacements
 * @return the formatted string
 */
export function format(fmt: string, ...values: (string | number)[]): string {
  console.assert(
    values !== null && values !== undefined && values.length > 0,
    'the string format function should not be used if no substitutions are necessary!'
  );
  if (!values) return 'error: no values passed for formatting';

  // reduce is a type-safe replacement for the imperative pattern of a loop wit `result = result.replace...`
  return values.reduce<string>(
    (strng, val, i) => strng.replace(`{${i}}`, val as string),
    fmt
  );
}

/**
 * Internationalize a language code to a language name.
 * Language code will be converted to a localized language name.
 *
 * This works like Sense.Language.resolveLanguageCode
 *
 * @param languageCode the language code of the Language e.g. 'en-US'
 * @param localeLanguageCode the language code of the language in which we want the Language to be displayed.
 * (Optional - defaults to languageCode)
 * @param showEnglish if a language name should also be shown in English (Optional - defaults to false)
 * @return localeLanguageName internationalized language name
 */
export function intlLanguageCode(
  languageCode: string,
  localeLanguageCode: string = languageCode,
  showEnglish = false
): string {
  const fallback = `${languageCode}-NO_VALUE_AVAILABLE`;
  const availableLanguages = window.EHSAvailableLanguages;
  if (
    isEmptyString(languageCode) ||
    isEmpty(availableLanguages) ||
    availableLanguages === undefined
  ) {
    return fallback;
  }

  // check if value for languageCode exists in availableLanguages
  languageCode = languageCode.trim().replace('-', '_');
  if (availableLanguages[languageCode] === undefined) {
    // no language with the given languageCode exists in availableLanguages
    // try to use the first 2 letters of the code
    // this means the languageCode 'en_GB' will turn to 'en'
    languageCode = languageCode.substring(0, 2);
    if (availableLanguages[languageCode] === undefined) return fallback;
  }

  // check if value for localeLanguageCode exists in availableLanguages
  localeLanguageCode = localeLanguageCode.trim().replace('-', '_');
  if (availableLanguages[languageCode][localeLanguageCode] === undefined) {
    // try to get value for the first two letters of localeLanguageCode
    localeLanguageCode = localeLanguageCode.substring(0, 2);
    if (availableLanguages[languageCode][localeLanguageCode] === undefined) {
      // no language for localeLanguageCode found
      // use languageCode as localeLanguageCode and try again
      localeLanguageCode = languageCode;
      if (availableLanguages[languageCode][localeLanguageCode] === undefined)
        return fallback;
    }
  }

  let localeLanguageName = availableLanguages[languageCode][localeLanguageCode];

  if (showEnglish && localeLanguageCode.substring(0, 2) !== 'en') {
    // add English language name
    localeLanguageName += ' - ';

    if (languageCode.length > 2) {
      localeLanguageName += availableLanguages[languageCode]['en_US'];
    } else {
      localeLanguageName += availableLanguages[languageCode]['en'];
    }
  }
  return localeLanguageName;
}

/**
 * Retrieve an internationalized string for the given key.
 *
 * This works like Sense.Language.get in extjs.
 *
 *
 * @param key The i18n key
 * @param fallback The optional fallback value to return
 * @return the internationalized string
 */
export function intl(
  key: LanguageProperty,
  fallback = `${key}-NO_VALUE_AVAILABLE`
): string {
  if (!window.EHSLanguageMap) {
    return key + '-NO_LANGUAGE_MAP_AVAILABLE';
  }
  if (key) {
    return window.EHSLanguageMap[key] ?? fallback;
  } else {
    return fallback;
  }
}

/**
 * Simultaneously internationalize and format a string using the given replacement values.
 *
 * @see intl
 * @see format
 *
 * @param key The i18n key
 * @param values Values to use for replacement in the internationalized string
 * @return the internationalized string with applied substitutions
 */
export function intlFmt(
  key: LanguageProperty,
  ...values: (string | number)[]
): string {
  return format(intl(key), ...values);
}

/**
 * Simultaneously internationalize and format a string using the given replacement values.
 *
 * IMPORTANT: Use this function only if {@link intlFmt} does not work!
 *
 * @param key the i18n key
 * @param map the key value pair to use in the replacement
 * @param fallback the internationalized fallback
 * @return the internationalized string with applied substitutions
 */
export function intlFmtTemplateString(
  key: LanguageProperty,
  map: Record<string, string>,
  fallback = `${key}-NO_VALUE_AVAILABLE`
): string {
  if (!window.EHSLanguageMap) {
    return key + '-NO_LANGUAGE_MAP_AVAILABLE';
  }

  let value: string;
  if (key) {
    value = window.EHSLanguageMap[key] ?? fallback;
  } else {
    value = fallback;
  }
  for (const languageKey of Object.keys(map)) {
    value = value.replace('${' + languageKey + '}', map[languageKey]);
  }

  return value;
}

/**
 * Type for the `substitutions` property of {@link intlJSX} and {@link formatJSX}.
 */
type JSXSubstitutions = Record<string, string | number | JSX.Element>;

/**
 * Simultaneously internationalize a string and substitute it's parameters with JSX elements.
 *
 * Note that when passing a JSX element, a `key` property needs to be set on it.
 * Using JSX (instead of HTML in the language string) like this helps prevent XSS attacks.
 * See {@link formatJSX} for more details and examples.
 *
 * If you need HTML in the translation, see {@link intlWithHtmlTranslation} - but then you cannot use substitutions / parameters!
 *
 * @note You can use this instead of using Ext.Tpl hacks. Having any kind of HTML inside the Language string won't work, so those need to be moved into the substitution (for example see {@link formatJSX}).
 * @param key The i18n key
 * @param substitutions object containing the replacement values to set for each defined replacement string
 */
export function intlJSX(
  key: LanguageProperty,
  substitutions: JSXSubstitutions
): JSX.Element {
  return formatJSX(intl(key), substitutions);
}

/**
 * Format a string using replacement values that can contain JSX elements.
 * Note that when passing a JSX element, a `key` property needs to be set on it.
 *
 * @example
 * let result = formatJSX('This is a {rep} test for {anotherRep} and {0}!', {
 *     rep: <strong key="rep">cool</strong>, // setting 'key' is important!
 *     anotherRep: 'no one',
 *     '0': 42
 * }
 * // resulting JSX: <>This is a <strong key="rep">cool</strong> test for no one and 42!</>
 *
 * // example 2:
 * result = formatJSX('{rep} test for {anotherRep}', {
 *     rep: <strong key="rep">cool</strong>,
 *     anotherRep: 'no one'
 * }
 * // resulting JSX: <><strong key="rep">cool</strong> test for no one</>
 *
 * @param fmt the format string to format (needs to contain replacement texts, e.g. '{0}', '{something}', etc.)
 * @param substitutions object containing the replacement values to set for each defined replacement string
 */
export function formatJSX(
  fmt: string,
  substitutions: JSXSubstitutions
): JSX.Element {
  const tokens = fmt.split(/{(\w+)}/);
  return (
    <>
      {tokens.map((token, index) => {
        // The token to substitute will always be in uneven positions, due to the splitting resulting in an empty string ('')
        // at the first/last position in cases where the replacement is at the first/last position
        if (index % 2 > 0) {
          return substitutions[token];
        }
        return token;
      })}
    </>
  );
}

/**
 * Internationalize a string and interpret it as HTML.
 *
 * This allows for linebreaks in the translation, but now you cannot use the return value as a string anymore.
 * Note that <strong>you cannot use substitutions/parameters</strong> in combination with this, as it would not be XSS-safe!
 *
 * If you need substitutions, use {@link intlJSX} instead - but then any HTML markup must be moved out to the substitutions.
 *
 * @example
 * <code>
 * // some.language.key=A long string <br/> with linebreaks <br/> between tests.
 * intlWithHtmlTranslation('some.language.key')
 * // resulting HTML: <span>A long string <br/> with linebreaks <br/> between tests.</span>
 * </code>
 *
 * @param key The i18n key
 * @param element The DOM element whose inner html to replace
 */
export function intlWithHtmlTranslation(
  key: LanguageProperty,
  element: 'div' | 'span' | 'p' = 'span'
): JSX.Element {
  // I'm not using JSX syntax here because I can't be bothered to switch over the possible `element` values.
  return React.createElement(element, {
    // NOTE: this is probably the only valid use-case for dangerouslySetInnerHTML.
    //  in other cases, you almost certainly actually want to use a sandboxed iframe, or intlJSX!
    dangerouslySetInnerHTML: { __html: intl(key) }
  });
}

/**
 * Get an internationalized string for the given country <a href="https://en.wikipedia.org/wiki/ISO_3166-1">ISO 3166-1</a> key.
 *
 * @param key the <a href="https://en.wikipedia.org/wiki/ISO_3166-1">ISO 3166-1</a> country code to translate
 * @return the translated string if it could be resolved, otherwise the given key is returned as is
 */
export function intlCountry(key: string): string {
  return window.EHSCountries?.[key] ?? key;
}

/**
 * Internationalized list of days in a week starting with Sunday.
 */
export const dayNames = [
  intl('ext.calendar.sunday'),
  intl('ext.calendar.monday'),
  intl('ext.calendar.tuesday'),
  intl('ext.calendar.wednesday'),
  intl('ext.calendar.thursday'),
  intl('ext.calendar.friday'),
  intl('ext.calendar.saturday')
];

/**
 * Internationalized list of days in a week starting with Monday.
 */
export const dayNamesFromMonday = [
  intl('ext.calendar.monday'),
  intl('ext.calendar.tuesday'),
  intl('ext.calendar.wednesday'),
  intl('ext.calendar.thursday'),
  intl('ext.calendar.friday'),
  intl('ext.calendar.saturday'),
  intl('ext.calendar.sunday')
];

/**
 * Internationalized list of days in a week starting with Sunday in short format.
 */
export const dayNamesShort = [
  intl('common.calendar.short.sunday'),
  intl('common.calendar.short.monday'),
  intl('common.calendar.short.tuesday'),
  intl('common.calendar.short.wednesday'),
  intl('common.calendar.short.thursday'),
  intl('common.calendar.short.friday'),
  intl('common.calendar.short.saturday')
];

/**
 * Internationalized list of months in a year.
 */
export const monthNames = [
  intl('ext.calendar.january'),
  intl('ext.calendar.february'),
  intl('ext.calendar.march'),
  intl('ext.calendar.april'),
  intl('ext.calendar.may'),
  intl('ext.calendar.june'),
  intl('ext.calendar.july'),
  intl('ext.calendar.august'),
  intl('ext.calendar.september'),
  intl('ext.calendar.october'),
  intl('ext.calendar.november'),
  intl('ext.calendar.december')
];
